import React from 'react';
import { formatDateDisplay, formatPriceCents } from 'utils';
import { IPurchaseOrder, ISupplierPurchaseOrderGroup } from 'services/BookingManagerApi/types/PurchaseOrdersResponse';
import { AccordionStackable } from 'ui/AccordionStackable/AccordionStackable';
import { Table } from 'ui/Table';
import { ITableColumn, ITableRow, ITableCell } from 'ui/Table/types';
import { ESortOrder } from 'store/common/types';
import { HidingTooltip } from 'ui/Tooltip';
import classNames from 'classnames';
import { SvgIcon } from 'ui/SvgIcon';
import InfoCircleIcon from 'ui/Icons/tabler/info-circle.component.svg';
import { ReviewStatusBadge } from 'ui/ReviewStatusBadge';
import { IPurchaseOrderDetailData } from './usePurchaseOrdersQueries';

interface ISupplierGroupAccordionProps {
  supplierGroup: ISupplierPurchaseOrderGroup;
  accordionKey: string;
  isOpen: boolean;
  onToggle: () => void;
  isUnassigned?: boolean;
  currencySymbol?: string;
  isPurchaseOrderReconciliationEnabled?: boolean;
  poDetailMap?: Record<string, IPurchaseOrderDetailData>;
}

const renderServiceTypeBadges = (serviceTypes: string[], isCancelled: boolean = false) => {
  if (!serviceTypes || serviceTypes.length === 0) return '-';

  return (
    <div className="flex flex-wrap gap-5px py-5px">
      {serviceTypes.map((serviceType, index) => (
        <div
          key={index}
          style={{
            maxWidth: 'min-content',
          }}
          className={classNames('bg-gray-10 font-pt-sans text-13px tracking-2xs px-5px py-5px rounded', {
            'text-gray-100 line-through': isCancelled,
            'text-black': !isCancelled,
          })}
        >
          {serviceType}
        </div>
      ))}
    </div>
  );
};

const renderDueDatesTooltip = (dueDates: any[], purchaseOrder: IPurchaseOrder, currencySymbol: string) => () => (
  <div
    className="bg-white border border-gray-40 rounded px-15px py-20px"
    style={{
      width: '250px',
      boxShadow:
        '0px 2px 4px -1px rgba(15, 23, 42, 0.06), 0px 4px 6px -1px rgba(15, 23, 42, 0.1), 0px 0px 1px 0px rgba(15, 23, 42, 0.06)',
    }}
  >
    {/* Green header section with invoice total */}
    <div className="bg-green-25 px-15px py-10px flex justify-between items-center">
      <span className="text-13px font-pt-sans text-gray-100 tracking-2xs uppercase">INVOICE TOTAL</span>
      <span className="text-15px font-pt-sans font-bold text-black">
        {currencySymbol}
        {formatPriceCents(purchaseOrder.totalCostCents)}
      </span>
    </div>

    {/* White body section with due dates */}
    <div className="bg-white-hard px-15px py-10px">
      <div className="text-13px font-pt-sans text-gray-100 tracking-2xs mb-5px">Due dates:</div>
      <div className="space-y-0">
        {dueDates.map((dueDate, index) => (
          <div key={index} className="flex justify-between items-center">
            <span className="text-13px font-pt-sans text-black">{formatDateDisplay(new Date(dueDate.date))}</span>
            <span className="text-13px font-pt-sans text-black text-right">
              {currencySymbol}
              {formatPriceCents(dueDate.amountCents)}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const renderPurchaseOrdersTable = (
  supplierGroup: ISupplierPurchaseOrderGroup,
  currencySymbol: string,
  isPurchaseOrderReconciliationEnabled: boolean,
  poDetailMap: Record<string, IPurchaseOrderDetailData>
) => {
  if (!supplierGroup.purchaseOrders || supplierGroup.purchaseOrders.length === 0) {
    return <div className="p-4 text-gray-60">No purchase orders found for this supplier group.</div>;
  }

  const columns: ITableColumn[] = [
    { id: 'poRef', label: 'PO REF.', width: '1fr' },
    { id: 'poDate', label: 'PO DATE', width: '1fr' },
    { id: 'confirmN', label: 'CONFIRM N.', width: '1.2fr' },
    ...(isPurchaseOrderReconciliationEnabled ? [{ id: 'rate', label: 'RATE', width: '69px' }] : []),
    { id: 'serviceDate', label: 'SERVICE DATE', width: '97px' },
    { id: 'serviceType', label: 'SERVICE TYPE', width: '110px' },
    { id: 'cancelled', label: '', width: 'auto' },
    { id: 'dueDates', label: 'DUE DATES', width: '80px', className: 'text-center p-0' },
    { id: 'cost', label: 'COST', width: '120px', className: 'text-right bg-green-25 border-l border-gray-20' },
    ...(isPurchaseOrderReconciliationEnabled
      ? [
          {
            id: 'invoice',
            label: 'INVOICE',
            width: '120px',
            className: 'text-right bg-green-25 border-l border-r border-gray-20',
          },
          { id: 'status', label: 'STATUS', width: '60px', className: 'text-center' },
        ]
      : []),
  ];

  const rows: ITableRow[] = supplierGroup.purchaseOrders.map((purchaseOrder: IPurchaseOrder, index: number) => {
    const isCancelled = purchaseOrder.cancelled;
    const detailData = poDetailMap[purchaseOrder.uuid];

    const cells: ITableCell[] = [
      {
        id: 'poRef',
        value:
          purchaseOrder.purchaseOrderCode ||
          (supplierGroup.supplierCode
            ? `PO-${supplierGroup.supplierCode}-${(index + 1).toString().padStart(3, '0')}`
            : '---'),
        className: classNames('text-15px font-pt-sans', {
          'text-gray-100 line-through': isCancelled,
          'text-black': !isCancelled,
        }),
      },
      {
        id: 'poDate',
        value: formatDateDisplay(new Date(purchaseOrder.createdAt)),
        className: classNames('text-15px font-pt-sans', {
          'text-gray-100 line-through': isCancelled,
          'text-black': !isCancelled,
        }),
      },
      {
        id: 'confirmN',
        value: purchaseOrder.confirmationNumber || '-',
        className: classNames('text-15px font-pt-sans', {
          'text-gray-100 line-through': isCancelled,
          'text-black': !isCancelled,
        }),
      },
      ...(isPurchaseOrderReconciliationEnabled
        ? [
            {
              id: 'rate',
              value: (
                <div
                  className={classNames('bg-gray-10 font-pt-sans text-13px tracking-2xs px-5px py-5px rounded', {
                    'text-gray-100 line-through': isCancelled,
                    'text-green-prime': !isCancelled && purchaseOrder.paidByVcc,
                    'text-black': !isCancelled && !purchaseOrder.paidByVcc,
                  })}
                  style={{ maxWidth: 'min-content' }}
                >
                  {purchaseOrder.paidByVcc ? 'Live' : 'Static'}
                </div>
              ),
            },
          ]
        : []),
      {
        id: 'serviceDate',
        value: purchaseOrder.firstServiceDate ? formatDateDisplay(new Date(purchaseOrder.firstServiceDate)) : '-',
        className: classNames('text-15px font-pt-sans', {
          'text-gray-100 line-through': isCancelled,
          'text-black': !isCancelled,
        }),
      },
      {
        id: 'serviceType',
        value: renderServiceTypeBadges(purchaseOrder.serviceTypes || [], isCancelled),
      },
      {
        id: 'cancelled',
        value: isCancelled ? <span className="text-13px font-pt-sans text-red-95 ml-3">Cancelled</span> : '',
      },
      {
        id: 'dueDates',
        value:
          purchaseOrder.dueDates && purchaseOrder.dueDates.length > 0 ? (
            <span className="w-full flex justify-center">
              <HidingTooltip
                renderTooltipContent={renderDueDatesTooltip(purchaseOrder.dueDates, purchaseOrder, currencySymbol)}
                position="left"
                tooltipClassname="mb-2 mr-2"
              >
                <SvgIcon
                  IconComponent={InfoCircleIcon}
                  className={classNames('cursor-pointer w-4 h-4', {
                    'fill-gray-20': isCancelled,
                    'fill-gray-60 hover:fill-gray-90': !isCancelled,
                  })}
                />
              </HidingTooltip>
            </span>
          ) : (
            <span
              className={classNames('text-15px font-pt-sans', {
                'text-gray-100': isCancelled,
                'text-black': !isCancelled,
              })}
            >
              -
            </span>
          ),
        className: 'text-center',
      },
      {
        id: 'cost',
        value: `${currencySymbol}${formatPriceCents(purchaseOrder.totalCostCents)}`,
        className: classNames('text-15px font-pt-sans text-right pr-10px border-l-1 border-gray-20', {
          'text-gray-100 line-through': isCancelled,
          'text-black': !isCancelled,
        }),
      },
      ...(isPurchaseOrderReconciliationEnabled
        ? [
            {
              id: 'invoice',
              value:
                detailData?.invoiceAmountCents != null
                  ? `${currencySymbol}${formatPriceCents(detailData.invoiceAmountCents)}`
                  : '-',
              className: classNames('text-15px font-pt-sans text-right pr-10px border-l-1 border-r-1 border-gray-20', {
                'text-gray-100 line-through': isCancelled,
                'text-black': !isCancelled,
              }),
            },
            {
              id: 'status',
              value: (
                <span className="w-full flex justify-center">
                  <ReviewStatusBadge status={detailData?.reviewStatus ?? null} />
                </span>
              ),
              className: 'text-center',
            },
          ]
        : []),
    ];

    return {
      id: `po-${index}`,
      cells,
      className: 'bg-white',
    };
  });

  return (
    <div className="border border-gray-20 rounded">
      <Table
        columns={columns}
        rows={rows}
        sortBy=""
        sortOrder={ESortOrder.ASC}
        onSort={() => {}}
        messageWhenEmpty="No purchase orders found for this supplier group."
        headerRowClassName="bg-white-hard text-xs font-pt-sans font-normal text-gray-100 uppercase tracking-2xs"
        className="border-collapse"
        noHeaderBorder={true}
        evenColor="white"
      />
    </div>
  );
};

export const SupplierGroupAccordion: React.FC<ISupplierGroupAccordionProps> = ({
  supplierGroup,
  accordionKey,
  isOpen,
  onToggle,
  isUnassigned = false,
  currencySymbol = '',
  isPurchaseOrderReconciliationEnabled = false,
  poDetailMap = {},
}) => {
  const poCount = supplierGroup.activePurchaseOrdersCount;
  const supplierName = isUnassigned
    ? 'Unassigned Purchase Orders'
    : supplierGroup.supplierName || `Supplier ${supplierGroup.supplierCode}` || 'Unknown Supplier';

  return (
    <AccordionStackable
      key={accordionKey}
      isOpen={isOpen}
      onToggle={onToggle}
      summary={
        <div className="flex justify-between items-center w-full mr-20px">
          <div className="flex flex-col justify-center gap-5px flex-1">
            <div
              className={classNames('text-15px font-pt-sans text-black', {
                'font-bold': isOpen,
                'font-normal': !isOpen,
              })}
            >
              {supplierName}
            </div>
            <div className="flex items-center gap-5px">
              <span className="text-13px font-pt-sans font-normal text-gray-100 tracking-2xs">
                {!isUnassigned && supplierGroup.supplierCode
                  ? `#${supplierGroup.supplierCode} - ${poCount} Purchase Order${poCount !== 1 ? 's' : ''}`
                  : `${poCount} Purchase Order${poCount !== 1 ? 's' : ''}`}
              </span>
            </div>
          </div>
          <div className="flex flex-col justify-center items-end gap-5px flex-1">
            {isOpen && (
              <div className="text-xs font-pt-sans font-normal text-gray-100 uppercase tracking-2xs text-right">
                TOTAL COST
              </div>
            )}
            <div
              className={classNames('font-pt-sans text-black text-right', {
                'text-17px font-bold': isOpen,
                'text-15px font-normal': !isOpen,
              })}
            >
              {currencySymbol}
              {formatPriceCents(supplierGroup.activePurchaseOrdersTotalCents)}
            </div>
          </div>
        </div>
      }
      wrapperClassName="bg-white border border-gray-20"
    >
      {renderPurchaseOrdersTable(supplierGroup, currencySymbol, isPurchaseOrderReconciliationEnabled, poDetailMap)}
    </AccordionStackable>
  );
};

export default SupplierGroupAccordion;
